import { useEffect, useState } from "react";
import { warn } from "@/lib/logger";

/**
 * Premium boot splash — shows once per session on first load with the mascot
 * over an emerald gradient. Fades out after ~1.4s. Tap anywhere to skip.
 */
export default function Splash() {
  const [visible, setVisible] = useState(() => {
    try { return !sessionStorage.getItem("fa_splash_shown"); } catch (err) {
      warn("Splash storage read failed:", err);
      return true;
    }
  });
  const [fading, setFading] = useState(false);

  const dismiss = () => {
    if (fading) return;
    setFading(true);
    setTimeout(() => {
      setVisible(false);
      try { sessionStorage.setItem("fa_splash_shown", "1"); } catch (err) {
        warn("Splash storage write failed:", err);
      }
    }, 400);
  };

  useEffect(() => {
    if (!visible) return;
    const t1 = setTimeout(() => setFading(true), 1300);
    const t2 = setTimeout(() => {
      setVisible(false);
      try { sessionStorage.setItem("fa_splash_shown", "1"); } catch (err) {
        warn("Splash storage write failed:", err);
      }
    }, 1900);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [visible]);

  if (!visible) return null;

  return (
    <div
      data-testid="splash"
      role="button"
      tabIndex={0}
      onClick={dismiss}
      onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && dismiss()}
      className="fixed inset-0 z-[200] flex flex-col items-center justify-center overflow-hidden cursor-pointer select-none"
      style={{
        background: "radial-gradient(ellipse at 50% 20%, #0F4F3B 0%, #0B1E1A 45%, #080E18 100%)",
        opacity: fading ? 0 : 1,
        transition: "opacity .4s ease-out",
        pointerEvents: fading ? "none" : "auto",
      }}
    >
      <div className="absolute inset-0 opacity-[.08] mix-blend-overlay pointer-events-none"
           style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.6'/%3E%3C/svg%3E\")" }} />
      <div className="absolute w-72 h-72 rounded-full pointer-events-none"
           style={{ background: "radial-gradient(circle, rgba(16,185,129,0.35) 0%, transparent 65%)", animation: "splashPulse 1.5s ease-out both" }} />
      <div className="relative w-40 h-40 sm:w-48 sm:h-48 rounded-3xl overflow-hidden ring-4 ring-emerald-500/50 shadow-[0_20px_60px_rgba(16,185,129,0.35)]"
           style={{ animation: "splashPop .7s cubic-bezier(.34,1.56,.64,1) both" }}>
        <img src="/logo.jpg" alt="AstaFantaLive" className="w-full h-full object-cover" />
      </div>
      <div className="mt-8 text-center pointer-events-none" style={{ animation: "splashFadeUp .8s .2s ease-out both" }}>
        <div className="font-black text-3xl sm:text-4xl tracking-tight text-white">Asta<span className="text-emerald-400">Fanta</span>Live</div>
        <div className="text-xs uppercase tracking-[0.4em] text-emerald-400/80 mt-2 font-bold">L'asta del fantacalcio</div>
      </div>
      <div className="absolute bottom-24 flex gap-1.5 pointer-events-none" style={{ animation: "splashFadeUp .8s .5s ease-out both" }}>
        {[0,1,2].map(i => (
          <span key={i} className="w-2 h-2 rounded-full bg-emerald-400"
                style={{ animation: `splashDot 1s ${i*0.15}s ease-in-out infinite` }} />
        ))}
      </div>
      <div className="absolute bottom-8 text-xs text-emerald-300/70 uppercase tracking-widest font-bold flex items-center gap-2 pointer-events-none"
           style={{ animation: "splashFadeUp .8s .9s ease-out both" }}
           data-testid="splash-skip-hint">
        Tocca per saltare
      </div>
      <style>{`
        @keyframes splashPop { 0% { opacity: 0; transform: scale(.6) rotate(-8deg) } 100% { opacity: 1; transform: scale(1) rotate(0) } }
        @keyframes splashPulse { 0% { opacity: 0; transform: scale(.4) } 60% { opacity: 1; transform: scale(1.15) } 100% { opacity: .3; transform: scale(1.4) } }
        @keyframes splashFadeUp { from { opacity: 0; transform: translateY(12px) } to { opacity: 1; transform: translateY(0) } }
        @keyframes splashDot { 0%, 100% { opacity: 0.25; transform: translateY(0) } 50% { opacity: 1; transform: translateY(-4px) } }
      `}</style>
    </div>
  );
}
